import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Button } from '@goaltree/ui/components/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { Input } from '@goaltree/ui/components/input'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import {
  Eye,
  Layers,
  MoreHorizontal,
  PencilLine,
  Plus,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import {
  GiveToClientDialog,
  GoalPickerDialog,
  TemplateOutlineDialog,
  toastLibraryError,
} from '@/components/coaching/coach-library'
import type { PlanSource } from '@/components/coaching/give-plan-dialog'
import { DiscoverTemplates, type TemplateFilter } from '@/components/discover-templates'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'
import { CATEGORY_COLORS } from '@/lib/category-color'

export const Route = createFileRoute('/_authenticated/coaching/library')({
  head: () => ({
    meta: [{ title: 'Library — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

type Mine = { _id: Id<'templates'>; title: string }

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const mine = useQuery(api.coachTemplates.mine)
  const save = useMutation(api.coachTemplates.saveFromGoal)
  const update = useMutation(api.coachTemplates.updateFromGoal)
  const rename = useMutation(api.coachTemplates.rename)
  const remove = useMutation(api.coachTemplates.remove)

  const [filter, setFilter] = useState<TemplateFilter>(null)
  const [picker, setPicker] = useState<{ updating: Mine | null } | null>(null)
  const [preview, setPreview] = useState<Id<'templates'> | null>(null)
  const [give, setGive] = useState<{ source: PlanSource; title: string } | null>(null)
  const [renaming, setRenaming] = useState<Mine | null>(null)
  const [name, setName] = useState('')
  const [deleting, setDeleting] = useState<Mine | null>(null)

  const givePrivate = (id: Id<'templates'>, title: string) =>
    setGive({ source: { kind: 'private', templateId: id }, title })

  const onPickGoal = async (goalId: Id<'nodes'>) => {
    const updating = picker?.updating
    try {
      if (updating) await update({ id: updating._id, goalId })
      else await save({ goalId })
      setPicker(null)
      toast.success(t(updating ? 'coachLibrary.updated' : 'coachLibrary.saved'))
    } catch (err) {
      toastLibraryError(err, t)
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader title={t('coachLibrary.title')} showInbox={false} />
        <SiteHeader className="hidden lg:flex" />
        <div className="flex-1 overflow-y-auto px-4 pb-16 lg:pb-10">
          <div className="mx-auto max-w-(--page-col) pt-6 lg:pt-10">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('coachLibrary.eyebrow')}
            </p>
            <h1 className="mt-1 text-2xl font-black tracking-tight">{t('coachLibrary.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('coachLibrary.desc')}</p>

            <section className="mt-6">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground/40">
                    {t('coachLibrary.mineTitle')}
                  </h2>
                  <p className="text-[11px] text-muted-foreground">{t('coachLibrary.mineHint')}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <button
                        type="button"
                        className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-coach px-3 py-1.5 text-xs font-bold text-white hover:bg-coach/90"
                      />
                    }
                  >
                    <Plus className="size-3.5" strokeWidth={2.5} />
                    {t('coachLibrary.newTemplate')}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem
                      className="cursor-pointer items-start"
                      onClick={() => setPicker({ updating: null })}
                    >
                      <Layers className="mt-0.5 size-4" />
                      <span>
                        <span className="block font-medium">{t('coachLibrary.fromGoal')}</span>
                        <span className="block text-xs text-muted-foreground">
                          {t('coachLibrary.fromGoalHint')}
                        </span>
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer items-start"
                      onClick={() => void navigate({ to: '/goals/new' })}
                    >
                      <Sparkles className="mt-0.5 size-4" />
                      <span>
                        <span className="block font-medium">{t('coachLibrary.fromScratch')}</span>
                        <span className="block text-xs text-muted-foreground">
                          {t('coachLibrary.fromScratchHint')}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {mine === undefined ? (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))] gap-2.5">
                  {[0, 1].map((i) => (
                    <div key={i} className="h-28 animate-pulse rounded-2xl bg-muted/40" />
                  ))}
                </div>
              ) : mine.length === 0 ? (
                <div className="rounded-2xl border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                  {t('coachLibrary.mineEmpty')}
                </div>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))] gap-2.5">
                  {mine.map((tpl) => (
                    <div
                      key={tpl._id}
                      className="flex min-w-0 flex-col gap-1.5 rounded-2xl border border-border bg-card p-3"
                    >
                      <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-muted-foreground uppercase">
                        <span
                          className="h-[7px] w-[7px] rounded-full"
                          style={{
                            backgroundColor: CATEGORY_COLORS[tpl.category] ?? CATEGORY_COLORS.mind,
                          }}
                        />
                        <span className="truncate">
                          {t(`discover.categories.${tpl.category}`, {
                            defaultValue: tpl.category,
                          })}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            render={
                              <button
                                type="button"
                                aria-label={t('coachLibrary.more')}
                                className="-my-1 ml-auto flex size-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                              />
                            }
                          >
                            <MoreHorizontal className="size-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => {
                                setName(tpl.title)
                                setRenaming(tpl)
                              }}
                            >
                              <PencilLine className="size-4" />
                              {t('coachLibrary.rename')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => setPicker({ updating: tpl })}
                            >
                              <RefreshCw className="size-4" />
                              {t('coachLibrary.updateFromGoal')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer text-destructive"
                              onClick={() => setDeleting(tpl)}
                            >
                              <Trash2 className="size-4" />
                              {t('coachLibrary.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="text-[13.5px] leading-snug font-bold">{tpl.title}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">
                        {[
                          t('templates.milestones', { count: tpl.milestoneCount }),
                          t('coachPlans.taskCount', { count: tpl.taskCount }),
                          ...(tpl.habitCount
                            ? [t('coachLibrary.habitCount', { count: tpl.habitCount })]
                            : []),
                        ].join(' · ')}
                      </p>
                      <div className="mt-auto flex items-center gap-2 border-t pt-2">
                        <button
                          type="button"
                          onClick={() => setPreview(tpl._id)}
                          className="inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                        >
                          <Eye className="size-3.5" />
                          {t('coachLibrary.preview')}
                        </button>
                        <button
                          type="button"
                          onClick={() => givePrivate(tpl._id, tpl.title)}
                          className="ml-auto inline-flex cursor-pointer items-center gap-1 text-xs font-bold text-primary-text hover:underline"
                        >
                          <Send className="size-3.5" />
                          {t('coachLibrary.give')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-10">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-foreground/40">
                {t('coachLibrary.libraryTitle')}
              </h2>
              <DiscoverTemplates
                filter={filter}
                onFilterChange={setFilter}
                hideMine
                cardAction={(tpl) => (
                  <button
                    type="button"
                    onClick={() =>
                      setGive({
                        source: { kind: 'template', templateId: tpl.id },
                        title: tpl.title,
                      })
                    }
                    className="inline-flex cursor-pointer items-center gap-1 text-[10.5px] font-bold text-primary-text hover:underline"
                  >
                    <Send className="size-3" />
                    {t('coachLibrary.give')}
                  </button>
                )}
              />
            </section>
          </div>
        </div>

        <GoalPickerDialog
          open={!!picker}
          title={
            picker?.updating
              ? t('coachLibrary.updateTitle', { title: picker.updating.title })
              : t('coachLibrary.pickGoalTitle')
          }
          onOpenChange={(o) => !o && setPicker(null)}
          onPick={onPickGoal}
        />
        <TemplateOutlineDialog
          templateId={preview}
          onClose={() => setPreview(null)}
          onGive={(id, title) => {
            setPreview(null)
            givePrivate(id, title)
          }}
        />
        <GiveToClientDialog plan={give} onClose={() => setGive(null)} />

        <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('coachLibrary.renameTitle')}</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!renaming) return
                try {
                  await rename({ id: renaming._id, title: name })
                  setRenaming(null)
                } catch (err) {
                  toastLibraryError(err, t)
                }
              }}
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
              <Button type="submit" className="mt-4 w-full" disabled={!name.trim()}>
                {t('coachLibrary.save')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('coachLibrary.deleteTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('coachLibrary.deleteDesc')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('coachLibrary.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={async () => {
                  if (!deleting) return
                  try {
                    await remove({ id: deleting._id })
                  } catch (err) {
                    toastLibraryError(err, t)
                  }
                  setDeleting(null)
                }}
              >
                {t('coachLibrary.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SidebarInset>
    </SidebarProvider>
  )
}
